import {
	type Task as ApiTask,
	type TaskProfile,
	type TaskSprint,
	type TaskTypeRef,
	type TaskGithubSummary,
	type ApiTaskStatus,
	type ApiTaskPriority,
} from "@/services/task.service";
import type { WorkflowStage } from "@/services/workflow-stage.service";
import type { WorkflowStagePlacementEntry } from "@/services/workflow-stage-catalog.service";
import { SYSTEM_STAGES } from "./system-stages";

// ── Types ─────────────────────────────────────────────────────────────────────

// ColumnId is now any workflow_stages.key for the task's project.
export type ColumnId = string;
export type Columns = Record<string, UiTask[]>;

export interface UiTask {
	id: string;
	project_id: string;
	ticket_id: string | null;
	ticket_code: string | null;
	title: string;
	description: string | null;
	developer_notes: string | null;
	apiStatus: ApiTaskStatus;
	columnId: ColumnId;
	sub_status: string | null;
	priority: ApiTaskPriority;
	assigned_to: ApiTask["assigned_to"];
	additional_assignees: ApiTask["additional_assignees"];
	created_by: ApiTask["created_by"] | null;
	due_date: string | null;
	tags: string[];
	estimated_time: number;
	active_sprint_estimated_time?: number | null;
	sprint: TaskSprint | null;
	parent_task_id: string | null;
	parent_task: {
		id: string;
		title: string;
		status: ApiTaskStatus;
		ticket_id: string | null;
	} | null;
	task_type_id: string | null;
	task_type: TaskTypeRef | null;
	has_calendar_event: boolean;
	checklist_total?: number;
	checklist_done?: number;
	github: TaskGithubSummary | null;
	created_at: string;
	updated_at: string;
}

// ── System-stage-derived display fallbacks ────────────────────────────────────
// These keep cross-project surfaces (sprint rows, dashboard charts) rendering
// the 6 system stages without each callsite needing a stages prop. Per-project
// surfaces that must show custom stages pass `stages` explicitly to the helpers
// below.

const STATUS_VARIANT: Record<
	string,
	"backlog" | "todo" | "in-progress" | "review" | "done" | "cancelled"
> = {
	backlog: "backlog",
	todo: "todo",
	"in-progress": "in-progress",
	qa: "review",
	done: "done",
	cancelled: "cancelled",
};

export const COLUMN_IDS: ColumnId[] = SYSTEM_STAGES.filter(
	(s) => s.key !== "cancelled",
).map((s) => s.key);

export const COLUMN_LABELS: Record<string, string> = Object.fromEntries(
	SYSTEM_STAGES.map((s) => [s.key, s.name]),
);

export const COLUMN_BADGE: Record<
	string,
	{
		variant: "backlog" | "todo" | "in-progress" | "review" | "done";
		dot: string;
	}
> = {
	backlog: { variant: "backlog", dot: "bg-muted" },
	todo: { variant: "todo", dot: "bg-muted" },
	"in-progress": { variant: "in-progress", dot: "bg-primary" },
	qa: { variant: "review", dot: "bg-secondary" },
	done: { variant: "done", dot: "bg-secondary" },
};

type StatusBadgeInfo = {
	variant:
		| "backlog"
		| "todo"
		| "in-progress"
		| "review"
		| "done"
		| "cancelled";
	label: string;
};

const SYSTEM_STATUS_BADGE: Record<string, StatusBadgeInfo> = Object.fromEntries(
	SYSTEM_STAGES.map((s) => [
		s.key,
		{ variant: STATUS_VARIANT[s.key] ?? "todo", label: s.name },
	]),
);

// Custom (non-system) workflow stages have no fixed variant/color here, since
// this map has no access to a project's stages list. Unknown keys fall back
// to a neutral badge instead of throwing, so callsites don't need per-project
// stage data just to render a badge. Callers that DO have the stages list
// should prefer getStageLabel/getStageColor for accurate custom-stage display.
export const STATUS_BADGE: Record<string, StatusBadgeInfo> = new Proxy(
	SYSTEM_STATUS_BADGE,
	{
		get(target, prop, receiver) {
			if (typeof prop === "string" && !(prop in target)) {
				return { variant: "todo", label: prop };
			}
			return Reflect.get(target, prop, receiver);
		},
	},
);

export const PRIORITY_BORDER: Record<ApiTaskPriority, string> = {
	urgent: "border border-danger/20 hover:border-danger/50 hover:shadow-danger/20",
	high: "border border-warning/20 hover:border-warning/50 hover:shadow-warning/20",
	medium: "border border-primary/20 hover:border-primary/50 hover:shadow-primary/20",
	low: "border border-gray-500/20 hover:border-gray-500/50 hover:shadow-gray-500/20",
};

export const AVATAR_COLORS = [
	"bg-primary",
	"bg-accent",
	"bg-secondary",
	"bg-warning",
	"bg-danger",
];

export const TIME_INCREMENTS: { label: string; delta: number }[] = [
	{ label: "+5 mins", delta: 5 },
	{ label: "+15 mins", delta: 15 },
	{ label: "+30 mins", delta: 30 },
	{ label: "+1 hour", delta: 60 },
	{ label: "+8 hours", delta: 60 * 8 },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

// Status and ColumnId are the same value (workflow_stages.key). These remain as
// identity functions to minimize churn at call sites.
export function apiStatusToColumnId(status: ApiTaskStatus): ColumnId {
	return status;
}

export function columnIdToApiStatus(colId: ColumnId): ApiTaskStatus {
	return colId;
}

export function toUiTask(t: ApiTask): UiTask {
	return {
		id: t.id,
		project_id: t.project_id,
		ticket_id: t.ticket_id ?? null,
		ticket_code: t.ticket?.ticket_code ?? null,
		title: t.title,
		description: t.description,
		developer_notes: t.developer_notes,
		apiStatus: t.status,
		columnId: t.status,
		sub_status: t.sub_status ?? null,
		priority: t.priority,
		assigned_to: t.assigned_to,
		additional_assignees: t.additional_assignees ?? [],
		created_by: t.created_by ?? null,
		due_date: t.due_date,
		tags: t.tags ?? [],
		estimated_time: t.estimated_time ?? 0,
		active_sprint_estimated_time: t.active_sprint_estimated_time ?? null,
		sprint: t.sprint ?? null,
		parent_task_id: t.parent_task_id ?? null,
		parent_task: t.parent_task ?? null,
		// Array.isArray(t.parent_task)
		// 	? (t.parent_task[0] ?? null)
		// 	: (t.parent_task ?? null),
		task_type_id: t.task_type_id ?? null,
		task_type: t.task_type ?? null,
		has_calendar_event: t.has_calendar_event ?? false,
		checklist_total: t.checklist_total,
		checklist_done: t.checklist_done,
		github: normalizeGithub(t.github),
		created_at: t.created_at,
		updated_at: t.updated_at,
	};
}

function normalizeGithub(
	g: ApiTask["github"],
): TaskGithubSummary | null {
	if (!g) return null;
	const link = Array.isArray(g) ? (g[0] ?? null) : g;
	if (!link || link.pr_state === undefined) return null;
	return link;
}

export function getStage(
	key: string,
	stages: WorkflowStage[] = SYSTEM_STAGES,
): WorkflowStage | undefined {
	return (
		stages.find((s) => s.key === key) ??
		SYSTEM_STAGES.find((s) => s.key === key)
	);
}

export function getStageLabel(key: string, stages?: WorkflowStage[]): string {
	return getStage(key, stages)?.name ?? key;
}

export function getStageColor(key: string, stages?: WorkflowStage[]): string {
	return getStage(key, stages)?.color ?? "#64748b";
}

export function sortStages(stages: WorkflowStage[]): WorkflowStage[] {
	return [...stages].sort((a, b) => a.position - b.position);
}

export function placementToWorkflowStage(
	entry: WorkflowStagePlacementEntry,
): WorkflowStage {
	return {
		id: entry.catalogId ?? `system-${entry.key}`,
		project_id: "",
		key: entry.key,
		name: entry.name,
		color: entry.color,
		position: entry.position,
		is_system: entry.is_system,
		projectIds: entry.is_system ? undefined : (entry.projectIds ?? []),
	};
}

export function emptyColumns(stages: WorkflowStage[] = SYSTEM_STAGES): Columns {
	const cols: Columns = {};
	for (const s of stages) cols[s.key] = [];
	return cols;
}

export function buildColumns(
	tasks: UiTask[],
	stages: WorkflowStage[] = SYSTEM_STAGES,
): Columns {
	const cols = emptyColumns(stages);
	for (const task of tasks) {
		const key = task.columnId;
		if (!cols[key]) cols[key] = [];
		cols[key].push(task);
	}
	return cols;
}

export function allAssignees(
	task: Pick<UiTask, "assigned_to" | "additional_assignees">,
): TaskProfile[] {
	return [task.assigned_to, ...task.additional_assignees].filter(
		(p): p is TaskProfile => !!p,
	);
}

export function getInitials(name: string | null | undefined): string {
	if (!name) return "?";
	return name
		.split(" ")
		.map((w) => w[0])
		.slice(0, 2)
		.join("")
		.toUpperCase();
}

export function profileColorClass(id: string): string {
	const sum = id.split("").reduce((acc, c) => acc + c.charCodeAt(0), 0);
	return AVATAR_COLORS[sum % AVATAR_COLORS.length];
}

export function formatTime(minutes: number): string {
	if (!minutes) return "0 min";
	if (minutes == 1) return "1 min";
	if (minutes < 60) return `${minutes} mins`;
	const hours = Math.floor(minutes / 60);
	const rem = minutes % 60;
	if (rem === 0) return `${hours} hr${hours > 1 ? "s" : ""}`;
	return `${hours} hr${hours > 1 ? "s" : ""} ${rem} min`;
}
